import queryString from 'querystring'

import { NextPage } from 'next'
import { withApollo } from 'components/withApollo'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import PageLayout from 'components/pages/PageLayout'
import { useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import toApiDateTime from 'utils/toApiDateTime'
import EventsForm, {
  FormValues
} from 'components/pages/EventsFormPage/EventsForm'
import BackButton from 'components/ui/BackButton'
import PageMeta from 'components/PageMeta'
import Link from 'next/link'
import getMutationErrors, {
  getMutationErrorData
} from 'utils/getMutationErrors'
import { useCateringRequestFormCategories } from 'components/pages/CateringRequestFormPage/types/CateringRequestFormCategories'
import { OptionGroup } from 'components/ui/DeepRadioGroup'
import sortBy from 'lodash/sortBy'
import pickBy from 'lodash/pickBy'
import identity from 'lodash/identity'
import { clearSavedFormValues } from 'components/FormValuesRecorder/helpers'
import { logEvent, setAdsUserData } from 'utils/analytics'
import ChatWidget from 'components/ChatWidget'
import { cn } from 'cn'

import { useTheme, withOrganizationTheme } from 'lib/theme'
import { WizardStep } from 'lib/theme/themes'
import { useCateringFormOrganizationQuery } from 'components/pages/CateringRequestFormPage/types/CateringFormOrganizationQuery'

import { User } from 'queries/useUser'

import { useEventRequestCreate } from './types/EventRequestCreate'

type Props = Record<string, never>

function EventsFormPage() {
  const { theme, isOrganizer } = useTheme()
  const router = useRouter()
  const [eventRequestCreate] = useEventRequestCreate({})

  const {
    organizer: organizerSeoName,
    event: initialCateringType,
    truckId: preferredTruckId,
    note: urlNote
  } = (router.query || {}) as {
    organizer?: string
    event?: string
    truckId?: string
    note?: string
  }

  useEffect(() => {
    if (urlNote) {
      sessionStorage.setItem('cateringNote', urlNote)
    }
  }, [urlNote])

  const { data: organizationData } = useCateringFormOrganizationQuery({
    variables: { name: organizerSeoName! },
    fetchPolicy: 'cache-first',
    skip: !organizerSeoName || !organizerSeoName.length
  })

  const { data: categoriesData } = useCateringRequestFormCategories({
    fetchPolicy: 'cache-first'
  })

  const categoryOptions: OptionGroup<string>[] = useMemo(() => {
    if (!categoriesData?.cateringCategories) return []

    return sortBy(
      categoriesData.cateringCategories.filter(
        (c) => !c.mainPage && !c.parentCategory
      ),
      'name'
    ).map((c): OptionGroup<string> => {
      const options = [
        ...sortBy(c.childCategories, 'name').map((child) => ({
          label: child.name,
          value: child.name
        }))
      ]
      if (c.childCategories.length > 0) {
        options.push({
          label: `${c.name} - Other`,
          value: `${c.name} - Other`
        })
      }
      return {
        label: c.name,
        value: c.name,
        options
      }
    })
  }, [categoriesData])

  const serializedQuery = queryString.stringify(
    pickBy(
      {
        truckId: preferredTruckId,
        event: initialCateringType,
        step: WizardStep.Frequency
      },
      identity
    )
  )

  const logoUrl =
    theme.logoUrl || organizationData?.organization?.cateringLogo?.variantUrl

  const themedBackground = isOrganizer && theme.page.themedBackground

  const backButtonHref = organizerSeoName
    ? `/catering/by/${organizerSeoName}/form?${serializedQuery}`
    : `/hire-food-truck?${serializedQuery}`

  const headerAction = <BackButton href={backButtonHref} />

  const organizationId = organizationData?.organization?.id

  const onSubmit = async (values: FormValues, user: User) => {
    const response = (await eventRequestCreate({
      variables: {
        eventType: values.eventType,
        startAt: toApiDateTime(values.startAt),
        endAt: toApiDateTime(values.endAt),
        fullAddress: values.fullAddress,
        vendingInstructions: values.vendingInstructions ?? null,
        peopleEstimated: parseInt(values.peopleEstimated as string, 10),
        creationNote: sessionStorage.getItem('cateringNote'),
        foodPreferences: values.foodPreferences,
        spots: parseInt(values.spots, 10),
        preferredTruckId: preferredTruckId
          ? parseInt(preferredTruckId, 10)
          : null,
        organizationId: organizationId ?? null
      }
    }))!

    const errors = getMutationErrors(response, {
      ...values,
      requestInProgress: true
    })

    if (errors) {
      if (errors.requestInProgress) {
        const errorData = getMutationErrorData<{ event_request_id: number }>(
          response,
          'requestInProgress'
        )
        errors.requestInProgress = (
          <>
            You already have an event request in progress. If you want to submit
            another request please cancel{' '}
            <Link
              className="underline"
              href={`/events/requests/${errorData!.event_request_id}`}
            >
              the existing request
            </Link>{' '}
            or contact support{' '}
            <a className="underline" href="sms:+18333780040">
              (833) 378-0040
            </a>
          </>
        )
      }
      return errors
    }

    await router.push({
      pathname: `/events/requests/${
        response.data!.eventsRequestCreate!.eventsRequest!.id
      }/trucks`,
      query: preferredTruckId
        ? {
            truckId: preferredTruckId
          }
        : {}
    })

    await setAdsUserData(user)

    logEvent('purchase', {
      transaction_id: response.data!.eventsRequestCreate!.eventsRequest!.id,
      currency: 'USD',
      value: 100,
      purchase_type: 'event_request',
      items: [
        {
          item_id: 'event_request',
          item_name: 'Event Request'
        }
      ]
    })

    // event_request_submission
    logEvent('conversion', {
      transaction_id: response.data!.eventsRequestCreate!.eventsRequest!.id,
      currency: 'USD',
      value: 100,
      send_to: 'AW-768772864/Z2czCLKCoIkZEICWyu4C',
      items: [
        {
          item_id: 'event_request',
          item_name: 'Event Request'
        }
      ]
    })

    clearSavedFormValues('CATERING_EVENT_FORM_VALUES')

    return undefined
  }

  return (
    <div className={cn({ 'bg-theme-primary': theme.page.themedBackground })}>
      <ChatWidget />

      <PageMeta
        canonicalPath="/events/form"
        noIndex={!!organizerSeoName}
        title="Best Food Trucks | Hire a food truck"
      />
      <PageLayout
        headerAction={headerAction}
        hideNavigation={!!organizerSeoName}
        gray={theme.page.grayBackground}
        hideFooter={theme.page.hideFooter}
        fullWidth={theme.page.fullWidth}
        staticPage
      >
        <div
          className={cn('min-h-screen w-full', {
            'bg-theme-primary font-theme-body': theme.page.themedBackground
          })}
        >
          <div className="flex items-center flex-col">
            {themedBackground ? (
              <>
                <div className="flex justify-center w-full pt-6 md:pt-10 mb-12">
                  <div className="flex flex-col items-center gap-2">
                    {logoUrl ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        alt={`${theme.name} logo`}
                        className="h-10 object-contain"
                        src={logoUrl}
                      />
                    ) : (
                      <span className="text-3xl font-bold text-white">
                        {theme.name}
                      </span>
                    )}
                    <span className="text-xs tracking-widest uppercase text-white/60">
                      Powered by{' '}
                      <span className="text-theme-accent font-semibold">
                        Best Food Trucks
                      </span>
                    </span>
                  </div>
                </div>
                <div className="text-center mb-12 px-4">
                  <h1 className="text-4xl md:text-5xl font-theme-heading font-bold text-white mb-4">
                    Request Food Trucks for Your Event
                  </h1>
                  <p className="text-white/70 text-lg max-w-2xl mx-auto">
                    Book food trucks for a single date where attendees order and
                    pay individually. Perfect for corporate events, block
                    parties, and community gatherings.
                  </p>
                </div>
              </>
            ) : (
              <>
                {!organizerSeoName && (
                  <div className="absolute top-20 left-0 right-0 z-10">
                    <div className="max-w-7xl mx-auto lg:mt-16 xl:mt-10 px-4">
                      <BackButton href={backButtonHref} />
                    </div>
                  </div>
                )}

                <div className="mt-16 xl:mt-10">
                  <h1 className="font-theme-heading font-bold text-theme-primary sm:text-5xl text-4xl text-center">
                    Request Food Trucks for Your Event
                  </h1>
                </div>
              </>
            )}

            <div className="flex flex-col max-w-screen-sm w-full pb-16">
              <div className="m-4 md:m-0">
                {!themedBackground && (
                  <div className="text-gray-700 text-center mt-4 mb-6">
                    Book food trucks for a single date where attendees order and
                    pay individually. Perfect for corporate events, block
                    parties, and community gatherings.
                  </div>
                )}
                <EventsForm
                  categoryOptions={categoryOptions}
                  initialCateringType={initialCateringType}
                  onSubmit={onSubmit}
                />
                {themedBackground && (
                  <div className="text-center mt-16 pb-12 text-sm text-white/60 px-4">
                    <p>
                      This event is organized by Best Food Trucks on behalf of{' '}
                      {theme.name}.
                    </p>
                    <p className="mt-1">
                      Questions?{' '}
                      {/* eslint-disable-next-line nextjs/no-html-link-for-pages */}
                      <a
                        className="text-theme-accent hover:underline"
                        href={`mailto:${
                          organizationData?.organization
                            ?.cateringEmailCustomer ||
                          'sales@bestfoodtrucks.com'
                        }`}
                      >
                        {organizationData?.organization
                          ?.cateringEmailCustomer || 'sales@bestfoodtrucks.com'}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    </div>
  )
}

const EventsFormPageWithApollo = withApollo({ ssr: false })(
  withOrganizationTheme(EventsFormPage) as NextPage
)
export default EventsFormPageWithApollo

// eslint-disable-next-line react/only-export-components
export const getStaticProps = getStaticApolloProps<Props>(EventsFormPage)
